import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { categoryService } from '../services/api';
import './CategoryForm.css';
import {
  Typography, Row, Col, Card, Input, InputNumber, Select, Switch, Button,
  Alert, Space, Divider, Tag, App, Empty, Tooltip
} from 'antd';
import { PlusOutlined, ImportOutlined, LinkOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

// ------- Shared filters (AdminFilters → CategoryForm, multi-support) -------
const FACET_DEFS = [
  { k: 'brand',          label: 'Marque',              type: 'select',      icon: '🏷️' },
  { k: 'power',          label: 'Puissance (W/kVA)',   type: 'select',      icon: '⚡' },
  { k: 'phase',          label: 'Phase',               type: 'select',      icon: '🔌' },
  { k: 'voltage',        label: 'Tension (V)',         type: 'select',      icon: '🔋' },
  { k: 'frequency',      label: 'Fréquence (Hz)',      type: 'select',      icon: '📊' },
  { k: 'technology',     label: 'Technologie',         type: 'select',      icon: '🧬' },
  { k: 'certification',  label: 'Certification/Norme', type: 'multiselect', icon: '✅' },
  { k: 'protection',     label: 'Indice Protection',   type: 'select',      icon: '🛡️' },
  { k: 'warranty',       label: 'Garantie (années)',   type: 'select',      icon: '📆' },
  { k: 'material',       label: 'Matériau/Carcasse',   type: 'select',      icon: '🧱' },
  { k: 'color',          label: 'Couleur',             type: 'select',      icon: '🎨' },
  { k: 'mounting',       label: 'Montage/Installation',type: 'select',      icon: '🔧' },
  { k: 'efficiency',     label: 'Rendement (%)',       type: 'select',      icon: '📈' },
  { k: 'batteryTech',    label: 'Batterie (Technologie)', type: 'select',   icon: '🔋' },
  { k: 'inverterType',   label: 'Type Onduleur',       type: 'select',      icon: '🔁' },
  { k: 'connector',      label: 'Connecteur/Sorties',  type: 'multiselect', icon: '🔗' },
  { k: 'origin',         label: 'Origine/Fabricant',   type: 'select',      icon: '🌍' },
];
const FACET_BY_KEY = FACET_DEFS.reduce((acc, f) => { acc[f.k] = f; return acc; }, {});

const STORAGE_KEY = 'domoelec:facetTemplates';
const SHARED_RAM_KEY = '__domoelecFiltersStore';
const CHANNEL_NAME = 'domoelec-filters-channel';
const EVENT_NAME = 'domoelec:filters-updated';

const DEFAULTS = {
  familyTree: [
    { id: 'fam_def_1', name: 'CONVENTIONNEL', children: [] },
    { id: 'fam_def_2', name: 'SOLAIRE - PHOTOVOLTAÏQUE', children: [] },
  ],
  templates: FACET_DEFS.reduce((acc, f) => { acc[f.k] = []; return acc; }, {}),
};

function normalizeAdminName(v) {
  return String(v || '')
    .trim()
    .toUpperCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/['']/g, "'");
}

const cloneTree = (t) => JSON.parse(JSON.stringify(t));

function genNodeId() {
  return 'n_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

function migrateNodeWithIds(n) {
  const node = cloneTree(n);
  if (!node.id) node.id = genNodeId();
  if (!node.name) node.name = 'Sans nom';
  if (!Array.isArray(node.children)) node.children = [];
  node.children = node.children.map(c => migrateNodeWithIds(c));
  return node;
}

function readSharedFilters() {
  try {
    // 1) RAM (globalThis)
    const ram = globalThis[SHARED_RAM_KEY];
    if (ram && ram.familyTree && ram.templates && ram._ts > Date.now() - 3600_000) {
      return cloneTree(ram);
    }
    // 2) sessionStorage
    try {
      const s = window.sessionStorage.getItem(STORAGE_KEY);
      if (s) {
        const parsed = JSON.parse(s);
        if (parsed && parsed.templates) return parsed;
      }
    } catch { /* */ }
    // 3) localStorage
    try {
      const l = window.localStorage.getItem(STORAGE_KEY);
      if (l) {
        const parsed = JSON.parse(l);
        if (parsed && parsed.templates) return parsed;
      }
    } catch { /* */ }
  } catch { /* */ }
  return null;
}

// ------- Composant principal -------
const CategoryForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { message: appMsg } = App.useApp();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentCategory: '',
    isActive: true,
    sortOrder: 0,
    metaTitle: '',
    metaDescription: '',
    specificFields: {},
  });

  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingData, setIsLoadingData] = useState(isEdit);
  const [error, setError] = useState(null);
  const setField = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));

  // AdminFilters synchronisés (familles + facettes)
  const [sharedTemplates, setSharedTemplates] = useState(DEFAULTS.templates);
  const [familyTree, setFamilyTree] = useState(DEFAULTS.familyTree);
  const [sharedFiltersVersion, setSharedFiltersVersion] = useState(0);

  const importFromShared = () => {
    const parsed = readSharedFilters();
    if (parsed && parsed.templates) setSharedTemplates(parsed.templates);
    if (parsed && parsed.familyTree) {
      const migrated = Array.isArray(parsed.familyTree)
        ? parsed.familyTree.map(migrateNodeWithIds)
        : DEFAULTS.familyTree.map(migrateNodeWithIds);
      setFamilyTree(migrated);
    }
    setSharedFiltersVersion(v => v + 1);
  };

  useEffect(() => {
    importFromShared();
  }, []);

  // BroadcastChannel + listeners (synchro AdminFilters → Catégorie en temps réel)
  useEffect(() => {
    let bc = null;
    try { bc = new BroadcastChannel(CHANNEL_NAME); } catch { /* */ }
    const onBC = (ev) => { if (ev?.data?.type === 'filters-updated') importFromShared(); };
    if (bc) bc.onmessage = onBC;
    const onCustom = () => importFromShared();
    window.addEventListener(EVENT_NAME, onCustom);
    const onStorage = (e) => { if (e.key === STORAGE_KEY) importFromShared(); };
    window.addEventListener('storage', onStorage);
    const onFocus = () => importFromShared();
    window.addEventListener('focus', onFocus);
    const interval = setInterval(importFromShared, 6000);
    return () => {
      if (bc) bc.close();
      window.removeEventListener(EVENT_NAME, onCustom);
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('focus', onFocus);
      clearInterval(interval);
    };
  }, []);

  // Chargement catégorie / catégories parentes
  useEffect(() => {
    loadCategories();
    if (isEdit) loadCategory();
  }, [id]);

  const loadCategories = async () => {
    try {
      const response = await categoryService.getCategories({ includeInactive: false });
      const filteredCategories = isEdit
        ? response.categories.filter(cat => cat.id !== id)
        : response.categories;
      setCategories(filteredCategories);
    } catch (err) {
      console.error('Erreur chargement catégories:', err);
    }
  };

  // 🔁 Polling backend 1.5s : nouvelle famille créée dans List Catégories apparait AUTOMATIQUEMENT
  useEffect(() => {
    const iv = setInterval(async () => {
      try {
        const response = await categoryService.getCategories({ includeInactive: false });
        const filteredCategories = isEdit
          ? response.categories.filter(cat => cat.id !== id)
          : (response.categories || []);
        setCategories(prev => {
          if (!prev) return filteredCategories;
          if (prev.length === filteredCategories.length) {
            const keyOf = x => String(x.id) + '|' + String(x.name) + '|' + String(x.parentCategoryId || 0) + '|' + (x.updatedAt || '');
            const setPrev = new Set(prev.map(keyOf));
            let same = true;
            for (const n of filteredCategories) { if (!setPrev.has(keyOf(n))) { same = false; break; } }
            if (same) return prev;
          }
          return filteredCategories;
        });
      } catch { /* ignore */ }
    }, 1500);
    return () => clearInterval(iv);
  }, [id, isEdit]);

  const loadCategory = async () => {
    try {
      setIsLoadingData(true);
      const response = await categoryService.getCategoryById(id);
      const cat = response.category;
      setFormData({
        name: cat.name || '',
        description: cat.description || '',
        parentCategory: cat.parentCategoryId || cat.parentCategory?.id || '',
        isActive: cat.isActive ?? true,
        sortOrder: cat.sortOrder || 0,
        metaTitle: cat.metaTitle || '',
        metaDescription: cat.metaDescription || '',
        specificFields: cat.specificFields || {},
      });
    } catch (err) {
      console.error('Erreur chargement catégorie:', err);
      setError('Erreur lors du chargement de la catégorie');
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!String(formData.name || '').trim()) {
      appMsg.error('Le nom de la catégorie est obligatoire');
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const submitData = { ...formData };
      if (isEdit) await categoryService.updateCategory(id, submitData);
      else await categoryService.createCategory(submitData);
      appMsg.success(isEdit ? '✅ Catégorie mise à jour' : '✅ Catégorie créée');
      navigate('/admin/categories');
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      const backendMessage = err?.response?.data?.message;
      setError(backendMessage || 'Erreur lors de la sauvegarde de la catégorie');
      appMsg.error(backendMessage || 'Erreur sauvegarde');
    } finally {
      setIsLoading(false);
    }
  };

  // ----- Chemin hiérarchique (Breadcrumb) depuis la racine jusqu'au parent sélectionné -----
  const buildHierarchyPath = useMemo(() => {
    const out = [];
    if (isEdit && id) {
      out.unshift({ id, name: formData.name || 'Catégorie actuelle', type: 'current' });
    }
    let currentId = formData.parentCategory ? String(formData.parentCategory) : null;
    let safety = 100;
    const byId = new Map((categories || []).map(c => [String(c.id), c]));
    while (currentId && safety > 0) {
      safety--;
      const cat = byId.get(String(currentId));
      if (!cat) break;
      out.unshift({ id: cat.id, name: cat.name, slug: cat.slug, type: cat.parentCategoryId ? 'subcategory' : 'family' });
      currentId = cat.parentCategoryId || cat.parentCategory?.id ? String(cat.parentCategoryId || cat.parentCategory?.id) : null;
    }
    out.unshift({ id: null, name: '🏠 Racine', type: 'root' });
    return out;
  }, [categories, formData.parentCategory, formData.name, id, isEdit]);

  // ---------- 🎯 CASCADE STRICTE 1→2→3 (Classification Produit, même logique Sidebar Publique) ----------
  const catById = useMemo(() => new Map((categories || []).map(c => [String(c.id), c])), [categories]);

  // Niveau 1 : FAMILLES RACINES (sans parent)
  const l1FamiliesOpts = useMemo(() => {
    const fams = (categories || []).filter(c =>
      !c.parentCategoryId && !(c.parentCategory && c.parentCategory.id) && String(c.id) !== String(id)
    );
    return [
      { value: '', label: '🏠 Aucune (nouvelle FAMILLE — niveau 0)', isRoot: true },
      ...fams.map(c => ({
        value: c.id,
        label: `${c.name}${(categories || []).some(ch => (String(ch.parentCategoryId) === String(c.id)) ? true : false) ? ' →' : ' ✓'}`,
        raw: c
      }))
    ];
  }, [categories, id]);

  // Initialiser cascade L1/L2/L3 depuis formData.parentCategory (EDIT MODE)
  const parentPathIds = useMemo(() => {
    if (!formData.parentCategory) return [];
    const out = [];
    let cur = String(formData.parentCategory);
    let safety = 50;
    while (cur && safety > 0) {
      safety--;
      const c = catById.get(cur);
      if (!c) break;
      out.unshift(c.id);
      cur = c.parentCategoryId || c.parentCategory?.id ? String(c.parentCategoryId || c.parentCategory?.id) : null;
    }
    return out; // ex: [idL1] ou [idL1,idL2] ou [idL1,idL2,idL3]
  }, [formData.parentCategory, catById]);

  const [cascadeL1, setCascadeL1] = useState(null); // sélection L1 (null = aucune)
  const [cascadeL2, setCascadeL2] = useState(null);
  const [cascadeL3, setCascadeL3] = useState(null);

  // Sync : quand parentPathIds change (loadCategory en EDIT OU cascade selections), alimente L1/L2/L3
  useEffect(() => {
    if (parentPathIds.length === 0) {
      setCascadeL1('');
      setCascadeL2(null);
      setCascadeL3(null);
    } else if (parentPathIds.length === 1) {
      setCascadeL1(parentPathIds[0]);
      setCascadeL2(null);
      setCascadeL3(null);
    } else if (parentPathIds.length === 2) {
      setCascadeL1(parentPathIds[0]);
      setCascadeL2(parentPathIds[1]);
      setCascadeL3(null);
    } else {
      setCascadeL1(parentPathIds[0]);
      setCascadeL2(parentPathIds[1]);
      setCascadeL3(parentPathIds[2]);
    }
    // eslint-disable-next-line
  }, [parentPathIds.join(',')]);

  // Children d'un parent donné (L1 → L2 options, L2 → L3 options, L3 → L4 options)
  const getChildrenOf = (parentId) => {
    if (!parentId) return [];
    const pidStr = String(parentId);
    return (categories || []).filter(c => (
      (String(c.parentCategoryId) === pidStr || (c.parentCategory && String(c.parentCategory.id) === pidStr))
      && String(c.id) !== String(id)
    ));
  };

  const l2Opts = useMemo(() => (cascadeL1 && cascadeL1 !== '') ? getChildrenOf(cascadeL1) : [], [cascadeL1, categories, id]);
  const l3Opts = useMemo(() => cascadeL2 ? getChildrenOf(cascadeL2) : [], [cascadeL2, categories, id]);
  const l4Opts = useMemo(() => cascadeL3 ? getChildrenOf(cascadeL3) : [], [cascadeL3, categories, id]);

  const l1Obj = cascadeL1 && cascadeL1 !== '' ? catById.get(String(cascadeL1)) : null;
  const l2Obj = cascadeL2 ? catById.get(String(cascadeL2)) : null;

  // Handlers (reset niveaux suivants, update parentCategory)
  const handleSelectL1 = (val) => {
    setCascadeL1(val);
    setCascadeL2(null);
    setCascadeL3(null);
    const parentId = (val === '' || val === null || val === undefined) ? '' : val;
    setField('parentCategory', parentId);
  };
  const handleSelectL2 = (val) => {
    setCascadeL2(val || null);
    setCascadeL3(null);
    if (val === undefined || val === null || val === '') {
      // Retour en L1 seulement
      setField('parentCategory', cascadeL1 || '');
    } else {
      setField('parentCategory', val);
    }
  };
  const handleSelectL3 = (val) => {
    setCascadeL3(val || null);
    if (val === undefined || val === null || val === '') {
      setField('parentCategory', cascadeL2 || cascadeL1 || '');
    } else {
      setField('parentCategory', val);
    }
  };
  const handleSelectL4 = (val) => {
    if (val === undefined || val === null || val === '') {
      setField('parentCategory', cascadeL3 || cascadeL2 || cascadeL1 || '');
    } else {
      setField('parentCategory', val);
    }
  };

  // ----- Actions sur Champs Spécifiques -----
  const addEmptySpecificField = () => {
    const key = `field_${Date.now()}`;
    const next = {
      ...formData.specificFields,
      [key]: { label: '', type: 'select', options: [], required: false }
    };
    setField('specificFields', next);
  };

  const importFacetFromAdmin = (facetKey) => {
    const facet = FACET_BY_KEY[facetKey];
    if (!facet) return;
    const tpl = sharedTemplates[facetKey] || [];
    const label = facet.label;
    // Cherche s'il existe déjà pour éviter doublons (par label normalisé)
    const normLabel = normalizeAdminName(label);
    const existingKey = Object.keys(formData.specificFields).find(k =>
      normalizeAdminName(formData.specificFields[k].label) === normLabel
    );
    const key = existingKey || `from_${facetKey}_${Date.now()}`;
    const existing = formData.specificFields[key] || { label: '', type: facet.type, options: [], required: false };
    const mergedOptions = Array.from(new Set([
      ...(existing.options || []),
      ...(Array.isArray(tpl) ? tpl : [])
    ]));
    const next = {
      ...formData.specificFields,
      [key]: {
        ...existing,
        label: existing.label || label,
        type: existing.type || facet.type,
        options: mergedOptions
      }
    };
    setField('specificFields', next);
    appMsg.success(`📥 Facette "${facet.icon} ${label}" importée (${mergedOptions.length} valeur(s))`);
  };

  const removeSpecificField = (fieldKey) => {
    const next = { ...formData.specificFields };
    const removedLabel = next[fieldKey]?.label || fieldKey;
    delete next[fieldKey];
    setField('specificFields', next);
    appMsg.success(`🗑️ Champ "${removedLabel}" retiré`);
  };

  const updateSpecificField = (fieldKey, patch) => {
    const next = {
      ...formData.specificFields,
      [fieldKey]: { ...(formData.specificFields[fieldKey] || {}), ...patch }
    };
    setField('specificFields', next);
  };

  if (isLoadingData) {
    return (
      <div className="category-form-page">
        <div className="loading-container">
          <i className="fas fa-spinner fa-spin"></i>
          <p>Chargement de la catégorie...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="category-form-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="page-title">
          <Title level={3} style={{ margin: 0 }}>
            {isEdit ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
          </Title>
          <Text type="secondary">
            {isEdit ? 'Modifiez les informations de la catégorie' : 'Créez une nouvelle catégorie'}
            {' · '}
            <Tag color="blue" style={{ marginTop: 6 }} icon={<LinkOutlined />}>
              Synchro Filtres Admin #{sharedFiltersVersion}
            </Tag>
          </Text>
        </div>
        <Space>
          <Button onClick={() => navigate('/admin/categories')}>Retour</Button>
          <Button type="primary" loading={isLoading} onClick={handleSubmit}>
            {isEdit ? 'Mettre à jour' : 'Créer la catégorie'}
          </Button>
        </Space>
      </div>

      {error && (
        <Alert type="error" showIcon title="Erreur" description={error} style={{ marginBottom: 16 }} />
      )}

      <form onSubmit={handleSubmit} className="category-form">
        <div className="form-grid">
          {/* Informations générales */}
          <Card className="form-section form-section--full" title="Informations générales">
            <div style={{ marginBottom: 16, padding: '12px 16px', background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderRadius: 8, border: '1px solid #bae6fd' }}>
              <Text strong style={{ display: 'block', marginBottom: 8, color: '#0369a1' }}>
                🏷️ Chemin hiérarchique
              </Text>
              <Space wrap size={[6, 6]}>
                {buildHierarchyPath.map((node, idx) => {
                  const isLast = idx === buildHierarchyPath.length - 1;
                  const levelNum = idx;
                  let tagColor = 'default';
                  let iconStr = '📍';
                  if (node.type === 'root') { tagColor = 'default'; iconStr = '🏠'; }
                  else if (node.type === 'family') { tagColor = 'blue'; iconStr = '🏠'; }
                  else if (node.type === 'subcategory') { tagColor = 'purple'; iconStr = '📁'; }
                  else if (node.type === 'current') { tagColor = 'cyan'; iconStr = '🍃'; }
                  return (
                    <React.Fragment key={node.id || `root-${idx}`}>
                      {idx > 0 && (
                        <Text type="secondary" style={{ fontSize: 12, lineHeight: 1 }}>›</Text>
                      )}
                      <Tag
                        color={tagColor}
                        style={{
                          fontSize: isLast ? 14 : 13,
                          fontWeight: isLast ? 700 : 500,
                          padding: '2px 10px',
                          border: isLast ? '2px solid #0891b2' : undefined
                        }}
                      >
                        <span style={{ marginRight: 4 }}>{iconStr}</span>
                        <Text strong style={{ marginRight: 4 }}>{levelNum}.</Text>
                        {node.type === 'family' ? String(node.name || '').toUpperCase() : node.name}
                      </Tag>
                    </React.Fragment>
                  );
                })}
              </Space>
            </div>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <Text strong style={{ fontSize: 17 }}>Nom de la catégorie *</Text>
                <Input
                  size="large"
                  value={formData.name}
                  onChange={(e) => setField('name', e.target.value)}
                  placeholder="Nom de la catégorie"
                  style={{ marginTop: 6 }}
                  required
                />
              </Col>

              <Col xs={24}>
                <div style={{ marginBottom: 14, marginTop: 8 }}>
                  <Text strong style={{ fontSize: 22, display: 'block', whiteSpace: 'nowrap' }}>📂 Classification de la catégorie — Cascade stricte</Text>
                  <Text type="secondary" style={{ marginLeft: 10, fontSize: 14 }}>
                    (même logique que « Classification du produit » et les filtres sidebar publique)
                  </Text>
                </div>
                <Row gutter={[16, 16]}>
                  {/* 1) NIVEAU 1 — FAMILLE (toujours affiché) */}
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <Text strong style={{ fontSize: 14, letterSpacing: 0.3, whiteSpace: 'nowrap', display: 'block' }}>
                      <span style={{ color: '#0369a1', marginRight: 6, fontWeight: 900, fontSize: 22 }}>1.</span>
                      FAMILLE
                    </Text>
                    <Select
                      size="large"
                      value={cascadeL1 === null ? undefined : cascadeL1}
                      onChange={handleSelectL1}
                      style={{ width: '100%', marginTop: 6 }}
                      showSearch
                      optionFilterProp="label"
                      allowClear
                      popupMatchSelectWidth={false}
                      listHeight={360}
                      dropdownStyle={{ minWidth: 420 }}
                      placeholder="Choisir une famille racine..."
                      options={l1FamiliesOpts}
                    />
                  </Col>

                  {/* 2) NIVEAU 2 — SOUS-CATÉGORIE (seulement si L1 choisi ET a des enfants) */}
                  {cascadeL1 && cascadeL1 !== '' && l2Opts.length > 0 && (
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Text strong style={{ fontSize: 12, letterSpacing: 0.3, whiteSpace: 'nowrap', display: 'block' }}>
                        <span style={{ color: '#0284c7', marginRight: 6, fontWeight: 900, fontSize: 22 }}>2.</span>
                        {l1Obj ? String(l1Obj.name || '').toUpperCase() : 'SOUS-CATÉGORIE'}
                      </Text>
                      <Select
                        size="large"
                        value={cascadeL2 ?? undefined}
                        onChange={handleSelectL2}
                        style={{ width: '100%', marginTop: 6 }}
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        popupMatchSelectWidth={false}
                        listHeight={360}
                        dropdownStyle={{ minWidth: 420 }}
                        placeholder="Sous-catégorie..."
                        options={l2Opts.map(c => ({
                          value: c.id,
                          label: `${c.name}${getChildrenOf(c.id).length > 0 ? ' →' : ' ✓'}`,
                          raw: c
                        }))}
                      />
                    </Col>
                  )}

                  {/* 3) NIVEAU 3 — SOUS-SOUS / SUITE (seulement si L2 choisi ET enfants) */}
                  {cascadeL2 && l3Opts.length > 0 && (
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Text strong style={{ fontSize: 12, letterSpacing: 0.3, whiteSpace: 'nowrap', display: 'block' }}>
                        <span style={{ color: '#0891b2', marginRight: 6, fontWeight: 900, fontSize: 22 }}>3.</span>
                        {l2Obj ? String(l2Obj.name || '').toUpperCase() : 'SOUS-SOUS-CATÉGORIE'}
                      </Text>
                      <Select
                        size="large"
                        value={cascadeL3 ?? undefined}
                        onChange={handleSelectL3}
                        style={{ width: '100%', marginTop: 6 }}
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        popupMatchSelectWidth={false}
                        listHeight={360}
                        dropdownStyle={{ minWidth: 420 }}
                        placeholder="Suite / 3ème niveau..."
                        options={l3Opts.map(c => ({
                          value: c.id,
                          label: `${c.name}${getChildrenOf(c.id).length > 0 ? ' →' : ' ✓'}`,
                          raw: c
                        }))}
                      />
                    </Col>
                  )}

                  {/* 4) NIVEAU 4 — FEUILLE PROFONDE */}
                  {cascadeL3 && l4Opts.length > 0 && (
                    <Col xs={24} sm={12} md={8} lg={6}>
                      <Text strong style={{ fontSize: 12, letterSpacing: 0.3, whiteSpace: 'nowrap', display: 'block' }}>
                        <span style={{ color: '#0e7490', marginRight: 6, fontWeight: 900, fontSize: 22 }}>4.</span>
                        {catById.get(String(cascadeL3)) ? String(catById.get(String(cascadeL3)).name || '').toUpperCase() : 'NIVEAU 4'}
                      </Text>
                      <Select
                        size="large"
                        value={formData.parentCategory && String(formData.parentCategory) !== String(cascadeL3) && String(formData.parentCategory) !== String(cascadeL2) && String(formData.parentCategory) !== String(cascadeL1) ? formData.parentCategory : undefined}
                        onChange={handleSelectL4}
                        style={{ width: '100%', marginTop: 6 }}
                        showSearch
                        optionFilterProp="label"
                        allowClear
                        popupMatchSelectWidth={false}
                        listHeight={360}
                        dropdownStyle={{ minWidth: 420 }}
                        placeholder="Feuille..."
                        options={l4Opts.map(c => ({
                          value: c.id,
                          label: `${c.name}${getChildrenOf(c.id).length > 0 ? ' →' : ' ✓'}`,
                          raw: c
                        }))}
                      />
                    </Col>
                  )}
                </Row>
                <div style={{ marginTop: 10 }}>
                  <Text type="secondary" style={{ fontSize: 15 }}>
                    📌 Règle : chaque niveau ne s'affiche que si le précédent est sélectionné et contient des enfants.
                    {formData.parentCategory ? (
                      <span> Parent final sélectionné : <Tag color="geekblue" icon={<LinkOutlined />}>{catById.get(String(formData.parentCategory))?.name || (formData.parentCategory === '' ? '🏠 AUCUN (famille racine)' : '—')}</Tag></span>
                    ) : (
                      <span> <Tag color="green" icon={<LinkOutlined />}>🏠 AUCUN — ce sera une nouvelle FAMILLE de niveau 0</Tag></span>
                    )}
                  </Text>
                </div>
              </Col>

              <Col xs={24} md={12}>
                <Text strong style={{ fontSize: 17 }}>Catégorie active</Text>
                <div style={{ marginTop: 8 }}>
                  <Switch checked={formData.isActive} onChange={(c) => setField('isActive', c)} style={{ transform: 'scale(1.2)', transformOrigin: 'left' }} />
                </div>
              </Col>
              <Col span={24}>
                <Text strong style={{ fontSize: 17 }}>Description</Text>
                <TextArea
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setField('description', e.target.value)}
                  placeholder="Description de la catégorie"
                  style={{ marginTop: 6, fontSize: 15 }}
                />
              </Col>
              <Col xs={24} md={12}>
                <Text strong style={{ fontSize: 17 }}>Ordre d'affichage</Text>
                <InputNumber
                  min={0}
                  size="large"
                  value={formData.sortOrder}
                  onChange={(v) => setField('sortOrder', v || 0)}
                  style={{ width: '100%', marginTop: 6 }}
                />
                <div className="form-help" style={{ fontSize: 14 }}>Les catégories sont triées par ordre croissant (0 = en premier)</div>
              </Col>
            </Row>
          </Card>

          {/* Champs spécifiques */}
          <Card
            className="form-section form-section--full"
            title={
              <Space>
                Champs spécifiques
                <Tag color="cyan" style={{ margin: 0 }} icon={<ImportOutlined />}>
                  Import 1-clic depuis Filtres Admin
                </Tag>
              </Space>
            }
            extra={
              <Tooltip title="Importe une FACETTE prédéfinie entière (label + type + OPTIONS remplies) depuis AdminFilters. Les valeurs ajoutées à AdminFilters dans un autre onglet s'ajoutent automatiquement.">
                <Select
                  style={{ width: 320, maxWidth: '100%' }}
                  placeholder="📥 Importer une facette depuis Filtres Admin..."
                  value={undefined}
                  onChange={importFacetFromAdmin}
                  showSearch
                  optionFilterProp="label"
                  options={FACET_DEFS.map(f => ({
                    value: f.k,
                    label: `${f.icon} ${f.label}${(sharedTemplates[f.k] || []).length ? ` (${(sharedTemplates[f.k] || []).length} val)` : ''}`
                  }))}
                  allowClear
                />
              </Tooltip>
            }
          >
            <div style={{ marginBottom: 16 }}>
              <Text type="secondary">
                Définissez les champs spécifiques qui apparaîtront dans le formulaire de produit pour cette catégorie.
                <br />
                <strong>Astuce :</strong> utilisez le select en haut à droite <Tag color="cyan">📥 Importer une facette depuis Filtres Admin</Tag> pour pré-remplir automatiquement Marque, Puissance, Phase, etc.
              </Text>
            </div>

            <div id="specific-fields-container">
              {Object.keys(formData.specificFields).length === 0 ? (
                <Empty
                  description={
                    <Space orientation="vertical" align="center" size={8} style={{ width: '100%' }}>
                      <Text type="secondary">Aucun champ spécifique défini pour cette catégorie.</Text>
                      <Text type="secondary">Utilisez « 📥 Importer une facette depuis Filtres Admin » OU ajoutez un champ vide.</Text>
                    </Space>
                  }
                  style={{ padding: 20 }}
                />
              ) : Object.entries(formData.specificFields).map(([fieldKey, cfg], index) => {
                const facetMatch = FACET_DEFS.find(f =>
                  normalizeAdminName(f.label) === normalizeAdminName(cfg.label)
                );
                const valsCount = (sharedTemplates[facetMatch?.k] || []).length;
                return (
                  <Card
                    key={fieldKey}
                    size="small"
                    style={{
                      marginBottom: 12,
                      border: `1px solid ${facetMatch ? '#bae6fd' : '#f0f0f0'}`,
                      background: facetMatch ? 'linear-gradient(90deg, #f0f9ff 0%, #ffffff 100%)' : '#fff'
                    }}
                    title={
                      <Space>
                        {facetMatch ? (
                          <Tag color="cyan" style={{ margin: 0 }} icon={<ImportOutlined />}>
                            {facetMatch.icon} FACETTE synchro AdminFilters
                            {valsCount ? ` · ${valsCount} val dispo` : ''}
                          </Tag>
                        ) : null}
                        <Tag color="default">Champ #{index + 1}</Tag>
                      </Space>
                    }
                    extra={
                      <Button
                        size="small"
                        danger
                        onClick={() => removeSpecificField(fieldKey)}
                      >
                        Supprimer
                      </Button>
                    }
                  >
                    <Row gutter={[12, 12]} align="middle">
                      <Col xs={24} md={6}>
                        <Text strong style={{ fontSize: 17 }}>Nom du champ</Text>
                        <Input
                          size="large"
                          value={cfg.label || ''}
                          onChange={(e) => updateSpecificField(fieldKey, { label: e.target.value })}
                          placeholder="Ex: Phase, Puissance"
                          style={{ marginTop: 6 }}
                        />
                      </Col>
                      <Col xs={24} md={4}>
                        <Text strong style={{ fontSize: 17 }}>Type</Text>
                        <Select
                          size="large"
                          value={cfg.type || 'select'}
                          onChange={(v) => updateSpecificField(fieldKey, { type: v })}
                          style={{ width: '100%', marginTop: 6 }}
                          options={[
                            { value: 'select', label: 'Liste déroulante' },
                            { value: 'multiselect', label: 'Sélection multiple' },
                          ]}
                        />
                      </Col>
                      <Col xs={24} md={10}>
                        <Text strong style={{ fontSize: 17 }}>
                          Options (virgule ou Entrée)
                          {facetMatch && (
                            <Tag color="cyan" style={{ marginLeft: 6 }}>
                              📥 Valeurs AdminFilters ajoutables en bas
                            </Tag>
                          )}
                        </Text>
                        <Select
                          mode="tags"
                          tokenSeparators={[',']}
                          value={Array.isArray(cfg.options) ? cfg.options : []}
                          onChange={(values) => {
                            const cleaned = (values || [])
                              .map(v => (typeof v === 'string' ? v.trim() : v))
                              .filter(v => v && String(v).length > 0);
                            updateSpecificField(fieldKey, { options: cleaned });
                          }}
                          style={{ width: '100%' }}
                          placeholder="Tapez une option puis , ou Entrée"
                        />
                        {facetMatch && valsCount > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              Propositions depuis AdminFilters (cliquez pour ajouter) :
                            </Text>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                              {(sharedTemplates[facetMatch.k] || []).slice(0, 20).map(optVal => {
                                const already = Array.isArray(cfg.options) && cfg.options.some(o => normalizeAdminName(o) === normalizeAdminName(optVal));
                                return (
                                  <Tag
                                    key={optVal}
                                    color={already ? 'green' : 'default'}
                                    style={{
                                      cursor: already ? 'default' : 'pointer',
                                      userSelect: 'none'
                                    }}
                                    onClick={() => {
                                      if (already) return;
                                      const merged = Array.from(new Set([
                                        ...(Array.isArray(cfg.options) ? cfg.options : []),
                                        optVal
                                      ]));
                                      updateSpecificField(fieldKey, { options: merged });
                                    }}
                                  >
                                    {already ? '✅ ' : '+ '}{optVal}
                                  </Tag>
                                );
                              })}
                              {(sharedTemplates[facetMatch.k] || []).length > 20 && (
                                <Tag color="blue">... +{(sharedTemplates[facetMatch.k] || []).length - 20}</Tag>
                              )}
                            </div>
                          </div>
                        )}
                      </Col>
                      <Col xs={24} md={4}>
                        <Text strong style={{ fontSize: 17 }}>Requis</Text>
                        <div style={{ marginTop: 8 }}>
                          <Switch
                            checked={Boolean(cfg.required)}
                            onChange={(c) => updateSpecificField(fieldKey, { required: c })}
                            style={{ transform: 'scale(1.2)', transformOrigin: 'left' }}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Card>
                );
              })}

              <Divider style={{ margin: '12px 0 14px' }} />

              <Space wrap style={{ width: '100%' }} size={10}>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={addEmptySpecificField}
                >
                  ➕ Ajouter un champ spécifique (vide)
                </Button>
                {FACET_DEFS.filter(f => (sharedTemplates[f.k] || []).length > 0).slice(0, 6).map(f => (
                  <Button
                    key={f.k}
                    ghost
                    type="primary"
                    onClick={() => importFacetFromAdmin(f.k)}
                  >
                    📥 {f.icon} {f.label} ({(sharedTemplates[f.k] || []).length})
                  </Button>
                ))}
              </Space>
            </div>
          </Card>

          {/* SEO */}
          <Card className="form-section form-section--full" title="Optimisation SEO">
            <Row gutter={[12, 12]}>
              <Col xs={24} md={12}>
                <Text strong style={{ fontSize: 17 }}>Meta Title</Text>
                <Input
                  size="large"
                  value={formData.metaTitle}
                  onChange={(e) => setField('metaTitle', e.target.value)}
                  maxLength={60}
                  placeholder="Titre pour les moteurs de recherche"
                  style={{ marginTop: 6 }}
                />
                <div className="form-help" style={{ fontSize: 14 }}>{formData.metaTitle.length}/60 caractères</div>
              </Col>
              <Col xs={24} md={12}>
                <Text strong style={{ fontSize: 17 }}>Meta Description</Text>
                <TextArea
                  rows={3}
                  value={formData.metaDescription}
                  onChange={(e) => setField('metaDescription', e.target.value)}
                  maxLength={160}
                  placeholder="Description pour les moteurs de recherche"
                  style={{ marginTop: 6, fontSize: 15 }}
                />
                <div className="form-help" style={{ fontSize: 14 }}>{formData.metaDescription.length}/160 caractères</div>
              </Col>
            </Row>
          </Card>
        </div>

        <div className="form-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <Button onClick={() => navigate('/admin/categories')}>Annuler</Button>
          <Button type="primary" htmlType="submit" loading={isLoading}>
            {isEdit ? 'Mettre à jour' : 'Créer la catégorie'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CategoryForm;
